import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import aiService from '../../services/aiService';
import { ReactComponent as OccasiaWhiteLogoIcon } from '../icons/occasia-white-logo-icon.svg';
import './AIChat.css';

// Occasia Logo component for header
const OccasiaLogo = () => (
  <div className="occasia-logo">
    <OccasiaWhiteLogoIcon className="occasia-logo-full" />
  </div>
);

// Send icon component
const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M16.5 1.5L8.25 9.75M16.5 1.5L11.25 16.5L8.25 9.75M16.5 1.5L1.5 6.75L8.25 9.75" 
      stroke="#056143" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// AI Avatar component
const AIAvatar = () => (
  <div className="ai-bot-avatar">
    <img src="/AIAvatar.svg" alt="AI Assistant" width="32" height="32" />
  </div>
);

const AIChat = ({ isOpen, onClose, userContext = {} }) => {
  const navigate = useNavigate();
  useCloseOnEscape(isOpen, onClose);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize with welcome message and pre-fetch venue data
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        type: 'ai',
        content: "👋 Want to chat about Occasia? I'm an AI chatbot here to help you find your way.",
        timestamp: new Date(),
        suggestions: [
          'Find venues for my event',
          'Check venue availability',
          'Calculate pricing',
          'Get help with booking'
        ]
      };
      setMessages([welcomeMessage]);
      setSuggestions(welcomeMessage.suggestions);
    }
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle mobile keyboard viewport changes
  useEffect(() => {
    if (!isOpen) return;

    const handleViewportResize = () => {
      // On mobile, when keyboard opens, the visualViewport height changes
      // This helps the chat widget adjust its height accordingly
      if (window.visualViewport) {
        const vh = window.visualViewport.height * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
    };

    // Set initial value
    handleViewportResize();

    // Listen for viewport changes (keyboard open/close)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }

    // Also listen for orientation changes
    window.addEventListener('orientationchange', handleViewportResize);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
      window.removeEventListener('orientationchange', handleViewportResize);
    };
  }, [isOpen]);

  // Handle sending message
  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setSuggestions([]);

    try {
      const response = await aiService.sendMessage(messageText, userContext);
      
      // Log the response source
      if (response.source) {
        if (response.source === 'gemini-ai') {
        } else if (response.source.includes('backend-fallback')) {
        }
      } else {
      }
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.message,
        timestamp: new Date(),
        data: response.data,
        responseType: response.type,
        suggestions: response.suggestions || [],
        source: response.source || 'unknown' // Store source for debugging
      };

      setMessages(prev => [...prev, aiMessage]);
      setSuggestions(aiMessage.suggestions);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
        suggestions: ['Try again', 'Get help', 'Find venues']
      };
      setMessages(prev => [...prev, errorMessage]);
      setSuggestions(errorMessage.suggestions);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    handleSendMessage(suggestion);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render message content based on type
  const renderMessageContent = (message) => {
    switch (message.responseType) {
      case 'venues':
        const venueData = message.data || [];
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {venueData.length > 0 && (
              <div className="venues-list">
                {venueData.slice(0, 3).map((venue, index) => {
                  const venueId = venue.id || venue.venue_id;
                  return (
                    <div key={index} className="venue-card-ai-chat">
                      <h4>{venue.name}</h4>
                      <p>Capacity: {venue.max_capacity || venue.capacity || 'N/A'} people</p>
                      <p>Price: £{venue.hourly_rate || venue.price || 'N/A'}/hour</p>
                      <p className="venue-address">{venue.address || venue.description || 'No description available'}</p>
                      {venueId && (
                        <button
                          className="book-venue-btn-ai"
                          onClick={() => {
                            onClose(); // Close chat modal
                            navigate(`/booking/${venueId}`);
                          }}
                        >
                          Book Now
                        </button>
                      )}
                    </div>
                  );
                })}
                {venueData.length > 3 && (
                  <p className="more-venues">...and {venueData.length - 3} more venues</p>
                )}
              </div>
            )}
          </div>
        );

      case 'pricing':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data && (
              <div className="pricing-breakdown">
                <div className="pricing-item">
                  <span>Venue:</span>
                  <span>{message.data.venue}</span>
                </div>
                <div className="pricing-item">
                  <span>Base Price:</span>
                  <span>£{message.data.basePrice}</span>
                </div>
                {message.data.serviceCosts > 0 && (
                  <div className="pricing-item">
                    <span>Services:</span>
                    <span>£{message.data.serviceCosts}</span>
                  </div>
                )}
                <div className="pricing-item total">
                  <span>Total:</span>
                  <span>£{message.data.totalPrice}</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'availability':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data && (
              <div className={`availability-status ${message.data.available ? 'available' : 'unavailable'}`}>
                <div className="status-indicator">
                  {message.data.available ? '✅' : '❌'}
                </div>
                <div className="status-text">
                  {message.data.available ? 'Available' : 'Not Available'}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="message-content">
            <p>{message.content}</p>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ai-chat-widget-wrapper" onClick={onClose}>
      <div className="ai-chat-widget" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-chat-header">
          <OccasiaLogo />
          <button className="ai-chat-close-btn" onClick={onClose} aria-label="Close chat">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Message History */}
        <div className="ai-chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`chat-message ${message.type}`}>
              {message.type === 'ai' && <AIAvatar />}
              <div className="message-bubble">
                {renderMessageContent(message)}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="chat-message ai">
              <AIAvatar />
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="ai-chat-suggestions">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isLoading}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Message Composer */}
        <div className="ai-chat-composer">
          <div className="composer-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Write a message"
              disabled={isLoading}
            />
          </div>
          <button
            className="send-button"
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            aria-label="Send message"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
